import React, { FC, useEffect, useMemo, useState } from 'react';
import { RideRoute } from '@component/Ride/RideRoute';
import { useCopiedLocationItems } from '@hooks/useCopiedLocationItems';
import { ArrowDownIcon, ChevronsIcon } from '@src/svg';
import { StatusLabelPill } from '@component/Lyft/StatusLabelPill';
import { useLyftSocket } from '@src/hooks';
import { allStatuses, toastError } from '@utils/helpers';
import { removeSeconds } from '@component/RidesPage/RidesTable/constants';
import {
  addOrSubtractDuration,
  addSeconds,
  formatTime,
  getEpochMsAtLocationLocalOffline,
  normalizeTime,
  redirectToExternalUrl,
  sumTimes,
} from '@src/utils';
import { toast } from 'react-toastify';
import Modal from '@src/uikit/Modal/Modal';
import '@component/SendMessageRides/sendMessageRides.scss';
import { convertTimeByCoords } from '@component/Ride/helper';
import { SendMessageRideCardItem } from '@component/SendMessageRideCard/SendMessageRideCardItem';
import { logger } from '@utils/logger';
import { useRideshareActionMutation } from '@store/rtk/auth';
import { toastMessage } from '@utils/toastMessage';

const CANCELLED_STATUS_IDS = [8, 13, 18, 19, 20, 25, 26, 27, 28];
const AFTER_ENROUTE_STATUS_IDS = [6, 7, 9, 10, 11, 12, 32];
const COMPLETED_STATUS_IDS = [
  7, 8, 9, 10, 11, 12, 13, 18, 19, 20, 25, 26, 27, 28,
];

const CANCEL_REASONS: string[] = [
  'My appointment was canceled.',
  'I am sick and cannot attend my appointment.',
  'I feel the weather is unsafe.',
  'I was transported by other means.',
];

type SendMessageRideCardProps = {
  id: string;
  segment: any;
  index: number;
  onChanged?: () => void;
  segments: any;
  dataTestId?: string;
};

export const SendMessageRideCard: FC<SendMessageRideCardProps> = ({
  segment,
  index,
  onChanged,
  id,
  segments,
  dataTestId,
}) => {
  const { locations } = useCopiedLocationItems({
    selectedSegment: segment,
    index,
  });

  const { socketStatus } = useLyftSocket();

  const [statusId, setStatusId] = useState<number | null>(
    segment?.status?.id ?? null
  );

  const [rideshareAction] = useRideshareActionMutation();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [actionState, setActionState] = useState<null | 'activate' | 'cancel'>(
    null
  );
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>(CANCEL_REASONS[0]);
  const [isReasonsOpen, setIsReasonsOpen] = useState(false);
  const [routeUrl, setRouteUrl] = useState<string | null>(
    segment?.rideSupportUrl || null
  );

  const [prevSegmentStatus, setPrevSegmentStatus] = useState<number | null>(
    null
  );

  useEffect(() => {
    const key = segment?.rideRemoteId ?? segment?.id;
    if (!key) return;

    if (index !== 0) {
      const prevSegmentObj = segments?.[index - 1];
      const prevKey = prevSegmentObj?.id;
      const socketPrevStatus = socketStatus?.[prevKey]?.statusId;

      setPrevSegmentStatus(
        socketPrevStatus ?? prevSegmentObj?.status?.id ?? null
      );
    }

    const socketData = socketStatus?.[key];
    if (!socketData) return;

    if (socketData.statusId) {
      setStatusId(socketData.statusId);
    }

    if (socketData.fullInfo?.route_url) {
      setRouteUrl(socketData.fullInfo.route_url);
    }
  }, [socketStatus, segment?.rideRemoteId, segment?.id, index, segments]);

  const getMarkerLabel = (locationIndex: number, defaultIndex: number) => {
    const marker = (locations?.[locationIndex] as any)?.marker;
    if (!marker?.label) return defaultIndex;
    return typeof marker.label === 'string' ? marker.label : marker.label.text;
  };

  const pickupLabel = useMemo(
    () => getMarkerLabel(0, index + 1),
    [locations, index]
  );
  const dropoffLabel = useMemo(
    () => getMarkerLabel(1, index + 2),
    [locations, index]
  );

  const effectiveStatusId = statusId ?? segment?.status?.id ?? null;

  const isAlreadyActivated = Boolean(
    segment.scheduledPickupTime && segment.callIndicator
  );

  const isActivateDisabled =
    !segment.callIndicator ||
    isAlreadyActivated ||
    isCancelled ||
    CANCELLED_STATUS_IDS.includes(effectiveStatusId as number) ||
    (index !== 0 &&
      !COMPLETED_STATUS_IDS.includes(prevSegmentStatus as number));
  // TODO check this properly
  // ||
  // !isWillCallActivationStatus(effectiveStatusId);

  const isCancelDisabled =
    isCancelled ||
    !effectiveStatusId ||
    CANCELLED_STATUS_IDS.includes(effectiveStatusId) ||
    AFTER_ENROUTE_STATUS_IDS.includes(effectiveStatusId);

  const handleTrackRide = () => {
    if (routeUrl) redirectToExternalUrl(routeUrl, true);
  };

  const handleInstantCancel = async (reason?: string) => {
    if (isCancelDisabled) return;
    setActionState('cancel');

    const body = {
      segmentId: segment.id,
      reason: reason,
      canceledBy: '',
      action: 'cancel' as const,
    };

    try {
      await rideshareAction({ body, params: { token: id } }).unwrap();
      setIsCancelled(true);
      toast.success(toastMessage.RIDE.rideSegmentWasCancelled);
    } catch (error: any) {
      logger.log('Cancel error:', error);
      toastError(error, toastMessage.RIDE.failedToCancelRideSegment2);
    } finally {
      setIsCancelModalOpen(false);
      setActionState(null);
    }
  };

  const computeWillCallCalculations = async (pickupTimeStr: string) => {
    try {
      const rideDate = segment?.rideDate;
      const pickup = segment?.pickupLocations;
      const dropoff =
        (segment as any)?.dropOffLocation || (segment as any)?.dropoffLocation;

      const origin =
        pickup?.latitude && pickup?.longitude
          ? { lat: Number(pickup.latitude), lng: Number(pickup.longitude) }
          : null;

      const destination =
        dropoff?.latitude && dropoff?.longitude
          ? { lat: Number(dropoff.latitude), lng: Number(dropoff.longitude) }
          : null;

      if (!rideDate || !pickupTimeStr || !origin || !destination) return null;

      const ms = getEpochMsAtLocationLocalOffline(
        rideDate,
        pickupTimeStr,
        origin.lat,
        origin.lng
      );

      const departureDate = ms > 0 ? new Date(ms) : new Date();

      let durationSec = 0;
      let distanceMeters = 0;

      try {
        const { getDrivingMatrixWithTraffic } = await import(
          '@src/components/Ride/dynamicImports'
        );

        const dm = await getDrivingMatrixWithTraffic({
          origins: [origin],
          destinations: [destination],
          departureTime: departureDate,
          trafficModel: google.maps.TrafficModel.BEST_GUESS,
        });

        const el = dm?.rows?.[0]?.elements?.[0];
        if (el && (el as any).status === 'OK') {
          durationSec =
            (el as any).duration_in_traffic?.value ?? el.duration?.value ?? 0;
          distanceMeters = el.distance?.value ?? 0;
        }
      } catch (e) {
        logger.error('Matrix Error:', e);
      }

      if (!durationSec || !distanceMeters) return null;

      const estimatedTripDuration = formatTime(durationSec, false);
      const miles = distanceMeters / 1609.344;
      const estMiles = `${miles.toFixed(1)} Mi`;

      const total = formatTime(
        sumTimes([pickupTimeStr, estimatedTripDuration]),
        false
      );

      const { normalizedTime } = normalizeTime(total, rideDate);

      return {
        scheduledAppointmentTime: normalizedTime,
        estimatedTripDuration,
        estMiles,
      };
    } catch (e) {
      logger.error('computeWillCallCalculations general error:', e);
      return null;
    }
  };

  const handleActivateRide = async () => {
    if (!segment?.id || isActivateDisabled) return;
    setActionState('activate');

    try {
      const now = addOrSubtractDuration(
        convertTimeByCoords(new Date(), segment?.pickupLocations),
        ['00:00'],
        true
      ).time;

      const computedFields = await computeWillCallCalculations(now);

      await rideshareAction({
        body: {
          segmentId: segment.id,
          estDropMiles: computedFields
            ? parseFloat(computedFields?.estMiles)
            : 0,
          estimatedDropTripDuration: computedFields
            ? addSeconds(computedFields?.estimatedTripDuration)
            : '',
          scheduledAppointmentTime: computedFields
            ? computedFields.scheduledAppointmentTime
            : '',
          scheduledPickupTime: now,
          action: 'activate',
        },
        params: { token: id },
      }).unwrap();

      toast.success(toastMessage.RIDE.rideActivated);
      onChanged?.();
    } catch (e: any) {
      logger.log('Activate ride error:', e);
      toastError(e, toastMessage.RIDE.failedToActivateRide);
    } finally {
      setActionState(null);
    }
  };

  return (
    <>
      <div
        data-test-id={`${dataTestId}-send-message-ride-card`}
        className="send-message-rides__card"
      >
        <div
          className="glob-body"
          data-test-id={`${dataTestId}-send-message-ride-card-body`}
        >
          <div
            data-test-id={`${dataTestId}-send-message-ride-card-header`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '15px 10px',
            }}
          >
            <div>
              <h2 style={{ fontWeight: 'bold', fontSize: '20px' }}>
                {segment?.scheduledPickupTime
                  ? removeSeconds(segment.scheduledPickupTime)
                  : 'Will Call'}
              </h2>
            </div>
            <StatusLabelPill
              label={
                (effectiveStatusId && allStatuses[effectiveStatusId]?.value) ||
                segment?.status?.label ||
                ''
              }
              segId={segment.id as number}
              style={effectiveStatusId ? allStatuses[effectiveStatusId] : {}}
              className="status-wrapper"
              dataTestId={`${dataTestId}-status-label-pill`}
            />
          </div>

          <div className="send-message-rides__route">
            <SendMessageRideCardItem
              label={pickupLabel}
              segmentLocation={segment.pickupLocations}
              dataTestId={`${dataTestId}-send-message-ride-card-item-pickupLocations`}
            />
            <div className="rides__arrows">
              <ChevronsIcon width={80} height={50} />
            </div>
            <SendMessageRideCardItem
              label={dropoffLabel}
              segmentLocation={segment.dropOffLocation}
              dataTestId={`${dataTestId}-send-message-ride-card-item-dropOffLocation`}
            />
          </div>

          <div className="rides__map">
            <RideRoute
              dataTestId={`${dataTestId}-send-message-ride-card-map`}
              height="200px"
              segmentsItemLocations={locations}
              suppressMarkers
              suppressPolylines
              preserveViewport={false}
              allowZoom
            />
          </div>
        </div>

        <div
          className="rides__actions"
          data-test-id={`${dataTestId}-send-message-ride-card-actions`}
        >
          <div
            className="rides__actions-right"
            data-test-id={`${dataTestId}-send-message-ride-card-actions-right`}
          >
            <button
              className="rides__btn"
              data-test-id={`${dataTestId}-send-message-ride-card-actions-right-activate-button`}
              onClick={handleActivateRide}
              disabled={isActivateDisabled || actionState === 'activate'}
            >
              {actionState === 'activate' ? '...Activating' : 'Activate ride'}
            </button>
            <button
              data-test-id={`${dataTestId}-send-message-ride-card-actions-right-track-button`}
              className="send-message-rides__btn"
              onClick={handleTrackRide}
              disabled={!routeUrl}
            >
              Track Driver
            </button>
          </div>
          <button
            data-test-id={`${dataTestId}-send-message-ride-card-actions-right-cancel-button`}
            className="rides__btn rides__btn--danger"
            onClick={() => setIsCancelModalOpen(true)}
            disabled={isCancelDisabled}
          >
            Cancel Ride
          </button>
        </div>
      </div>

      {isCancelModalOpen && (
        <Modal
          title="Cancel Ride"
          maxWidth="320px"
          closeClick={() => setIsCancelModalOpen(false)}
        >
          <div className="cancel-ride-modal">
            <div className="cancel-modal__body">
              <label className="cancel-modal__label">Cancellation Reason</label>
              <div className="cancel-modal__select-wrapper">
                <button
                  data-test-id={`${dataTestId}-send-message-ride-card-cancel-modal-select-button`}
                  type="button"
                  className="cancel-modal__select-button"
                  onClick={() => setIsReasonsOpen(!isReasonsOpen)}
                >
                  <span>{cancelReason}</span>
                  <ArrowDownIcon color={'#fff'} />
                </button>
                {isReasonsOpen && (
                  <ul
                    className="cancel-modal__dropdown"
                    data-test-id={`${dataTestId}-send-message-ride-card-cancel-modal-dropdown`}
                  >
                    {CANCEL_REASONS.map((reason) => (
                      <li
                        key={reason}
                        data-test-id={`${dataTestId}-send-message-ride-card-cancel-modal-dropdown-option-${reason}`}
                        className="cancel-modal__option"
                        onClick={() => {
                          setCancelReason(reason);
                          setIsReasonsOpen(false);
                        }}
                      >
                        {reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="cancel-modal__footer">
              <button
                data-test-id={`${dataTestId}-send-message-ride-card-cancel-modal-submit-button`}
                className="rides__btn rides__btn--danger cancel-modal__submit"
                onClick={handleInstantCancel.bind(null, cancelReason)}
              >
                {actionState === 'cancel' ? '...Canceling' : 'Cancel Ride'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SendMessageRideCard;
